import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, concatMap, EMPTY, filter, map, of, switchMap, tap } from 'rxjs';

import { NotificationService } from '@blaze-shared/platform-data-access';
import { DialogService } from '@blaze-shared/shared-feature';
import { StylesApiService } from '@blaze-shared/theming-data-access';
import { CreateStyleSheetRequest, UpdateStyleSheetRequest } from '@blaze-shared/theming-domain';

import { mapPropsToStyleData, mapStyleDataToProps } from './style-mappers';
import { mergeElementAndStyleProps } from './style-merger';
import * as StylesActions from './styles.actions';
import { selectCurrentStylesProjectId, selectStyleSheetById } from './styles.selectors';

@Injectable()
export class StylesEffects {
  private readonly actions$ = inject(Actions);
  private readonly notificationService = inject(NotificationService);
  private readonly stylesApisService = inject(StylesApiService);
  private readonly dialogService = inject(DialogService);
  private readonly store = inject(Store);

  loadStyleSheetsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StylesActions.loadStylesSheets),
      filter((projectId) => !!projectId),
      switchMap(({ projectId, includeReferences }) => {
        if (!projectId) {
          // Can happen when navigating into a project and quickly going back to the project list
          return EMPTY;
        }
        return this.stylesApisService.getAllStyleSheets(projectId, includeReferences).pipe(
          map((styleSheets) =>
            StylesActions.loadStyleSheetsReadyForMigration({
              styleSheets,
              updateMode: includeReferences,
              projectId,
            }),
          ),
          catchError((error) =>
            of(StylesActions.loadStyleSheetsFailure({ error: error?.error?.errorMessage })),
          ),
        );
      }),
    ),
  );

  refreshStylesheets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        StylesActions.createStyleSheetSuccess,
        StylesActions.updateStyleSheetSuccess,
        StylesActions.setDefaultStyleSheetSuccess,
        StylesActions.refreshStyleSheets,
      ),
      concatLatestFrom(() => this.store.select(selectCurrentStylesProjectId)),
      switchMap(([, projectId]) => {
        if (!projectId) {
          return EMPTY;
        }
        return this.stylesApisService.getAllStyleSheets(projectId, true).pipe(
          map((styleSheets) =>
            StylesActions.loadStyleSheetsReadyForMigration({
              styleSheets,
              updateMode: true,
              projectId,
            }),
          ),
        );
      }),
    ),
  );

  createStyleSheetRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StylesActions.createStyleSheet),
      map(
        ({
          name,
          projectId,
          elementId,
          slotId,
          elementType,
          props,
          overrides,
          setDefault,
          themeId,
          version,
        }) => {
          const request: CreateStyleSheetRequest = {
            name,
            projectId,
            themeId: themeId,
            isThemeDefault: !!setDefault,
            elementType,
            data: mapPropsToStyleData(props, overrides),
            version,
          };
          return { request, elementId, slotId };
        },
      ),
      concatMap(({ request, elementId, slotId }) => {
        return this.stylesApisService.postStyleSheet(request).pipe(
          tap((styleSheet) => {
            this.notificationService.success(`Created style ${styleSheet.name}`);
          }),
          map((styleSheet) => {
            return StylesActions.createStyleSheetSuccess({ styleSheet, elementId, slotId });
          }),
        );
      }),
      catchError((error) =>
        of(StylesActions.createStyleSheetFailure({ error: error?.errorMessage || error?.message })),
      ),
    ),
  );

  updateStyleSheetRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StylesActions.updateStyleSheet),
      concatLatestFrom(({ id }) => this.store.select(selectStyleSheetById(id))),
      filter(([_, styleSheet]) => !!styleSheet),
      map(
        ([
          {
            current,
            elementId,
            elementType,
            id,
            name,
            overrides,
            props,
            setDefault,
            slotId,
            version,
          },
        ]) => {
          const currentStyleProps = mapStyleDataToProps(current.data ?? []);
          const styleProps = mergeElementAndStyleProps(
            props,
            overrides,
            currentStyleProps.props,
            currentStyleProps.overrides,
          );
          const request: UpdateStyleSheetRequest = {
            name,
            elementType,
            projectId: current.projectId ?? '',
            themeId: current.themeId ?? '',
            isThemeDefault: !!setDefault,
            data: mapPropsToStyleData(styleProps.props, styleProps.overrides),
            version,
          };
          return { id, request, elementId, slotId };
        },
      ),
      concatMap(({ id, request, elementId, slotId }) => {
        return this.stylesApisService.putStyleSheet(id, request).pipe(
          tap((styleSheet) => {
            this.notificationService.success(`Updated style ${styleSheet.name}`);
          }),
          map((styleSheet) =>
            StylesActions.updateStyleSheetSuccess({ styleSheet, elementId, slotId }),
          ),
        );
      }),
      catchError((error) =>
        of(StylesActions.updateStyleSheetFailure({ error: error?.errorMessage || error?.message })),
      ),
    ),
  );

  setDefaultStyleSheet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StylesActions.setDefaultStyleSheet),
      concatLatestFrom(({ id }) => [this.store.select(selectStyleSheetById(id))]),
      switchMap(([{ id, hideNotification }, styleSheet]) => {
        if (!styleSheet) {
          throw new Error(`Could not find style sheet of id: ${id} `);
        }
        const request: UpdateStyleSheetRequest = {
          ...styleSheet,
          isThemeDefault: !styleSheet.isThemeDefault,
          version: styleSheet.version ?? 1,
        };
        return this.stylesApisService.putStyleSheet(id, request).pipe(
          tap((styleSheet) => {
            if (!hideNotification) {
              this.notificationService.success(`Updated style ${styleSheet.name}`);
            }
          }),
          map((styleSheet) => StylesActions.setDefaultStyleSheetSuccess({ styleSheet })),
        );
      }),
      catchError((error) =>
        of(StylesActions.updateStyleSheetFailure({ error: error?.errorMessage || error?.message })),
      ),
    ),
  );

  deleteStyleSheetRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StylesActions.deleteStyleSheet),
      concatLatestFrom(({ id }) => this.store.select(selectStyleSheetById(id))),
      map(([_, styleSheet]) => styleSheet),
      filter(Boolean),
      switchMap((styleSheet) =>
        this.dialogService
          .openConfirmDialog({
            title: 'Confirm style delete',
            description: `Do you want to delete ${styleSheet.name}`,
            cancelLabel: 'Cancel',
            confirmLabel: 'Yes',
          })
          .afterClosed()
          .pipe(map((confirmation) => ({ confirmation, styleSheet }))),
      ),
      filter(({ confirmation }) => !!confirmation),
      switchMap(({ styleSheet }) =>
        this.stylesApisService.deleteStyleSheet(styleSheet.id).pipe(
          tap(() => {
            this.notificationService.success(`Deleted style ${styleSheet.name}`);
          }),
          map(() => StylesActions.deleteStyleSheetSuccess({ id: styleSheet.id })),
        ),
      ),
      catchError((error) =>
        of(StylesActions.deleteStyleSheetFailure({ error: error?.errorMessage || error?.message })),
      ),
    ),
  );

  errorHandler$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          StylesActions.loadStyleSheetsFailure,
          StylesActions.createStyleSheetFailure,
          StylesActions.updateStyleSheetFailure,
          StylesActions.deleteStyleSheetFailure,
        ),
        tap(({ error }) => {
          this.notificationService.error(error);
        }),
      ),
    { dispatch: false },
  );
}
